import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Info, Loader2, X, TrendingUp, Award, MapPin, ShieldCheck, BarChart3, Users } from "lucide-react";

const ICON_BY_KEY = {
  naics: BarChart3,
  geo: MapPin,
  setaside: ShieldCheck,
  pp: Award,
  band: TrendingUp,
  peer: Users,
};

function toneFor(pct) {
  if (pct >= 0.75) return { bar: "bg-emerald-500", tile: "text-emerald-600 bg-emerald-50 border-emerald-200", label: "STRONG" };
  if (pct >= 0.45) return { bar: "bg-amber-500", tile: "text-amber-700 bg-amber-50 border-amber-200", label: "OK" };
  if (pct >= 0.15) return { bar: "bg-orange-500", tile: "text-orange-700 bg-orange-50 border-orange-200", label: "WEAK" };
  return { bar: "bg-slate-300", tile: "text-slate-500 bg-slate-100 border-slate-200", label: "MISSING" };
}

/**
 * Popover button that surfaces the deterministic breakdown of the
 * bid/no-bid win probability. Renders inline next to the AI verdict.
 */
export default function WinProbabilityExplainer({ noticeId, aiProbability }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const btnRef = useRef(null);
  const popRef = useRef(null);

  useEffect(() => {
    if (!open || data) return;
    setLoading(true);
    (async () => {
      try {
        const r = await api.get(`/opportunities/${noticeId}/win-signals`);
        setData(r.data);
      } catch (e) {
        setData({ error: e?.response?.data?.detail || "Failed to load win signals" });
      } finally { setLoading(false); }
    })();
  }, [open, noticeId, data]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (popRef.current?.contains(e.target)) return;
      if (btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onEsc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  return (
    <span className="relative inline-flex">
      <button
        ref={btnRef}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest px-2 py-1 rounded border border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100"
        data-testid="win-explainer-btn"
      >
        <Info className="w-3 h-3" /> Why?
      </button>
      {open && (
        <div
          ref={popRef}
          className="absolute z-40 top-full mt-2 right-0 w-[380px] max-w-[92vw] rounded-2xl border border-slate-200 bg-white shadow-2xl p-5"
          data-testid="win-explainer-popover"
        >
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-violet-600">// WIN PROBABILITY EXPLAINER</div>
              <div className="font-display font-bold text-lg">Why this score?</div>
            </div>
            <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700" data-testid="win-explainer-close"><X className="w-4 h-4" /></button>
          </div>

          {loading && <div className="flex items-center gap-2 text-slate-500 text-sm" data-testid="win-explainer-loading"><Loader2 className="w-4 h-4 animate-spin" />Analyzing signals…</div>}
          {data?.error && <div className="text-sm text-rose-600" data-testid="win-explainer-error">{data.error}</div>}

          {data && !data.error && (
            <>
              <div className="grid grid-cols-3 gap-2 mb-4">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-2 text-center" data-testid="win-explainer-composite">
                  <div className="text-2xl font-display font-black text-slate-900">{data.composite_score}</div>
                  <div className="text-[9px] font-mono uppercase tracking-widest text-slate-500">Composite</div>
                </div>
                <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-2 text-center" data-testid="win-explainer-match">
                  <div className="text-2xl font-display font-black text-indigo-700">{data.match_score ?? "—"}</div>
                  <div className="text-[9px] font-mono uppercase tracking-widest text-indigo-500">Match</div>
                </div>
                <div className="rounded-lg border border-fuchsia-200 bg-fuchsia-50 p-2 text-center" data-testid="win-explainer-ai">
                  <div className="text-2xl font-display font-black text-fuchsia-700">{data.ai_win_probability ?? aiProbability ?? "—"}{(data.ai_win_probability ?? aiProbability) != null ? "%" : ""}</div>
                  <div className="text-[9px] font-mono uppercase tracking-widest text-fuchsia-500">AI Verdict</div>
                </div>
              </div>

              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">// TOP 3 SIGNALS</div>
              <div className="space-y-2 mb-3">
                {(data.top_signals || []).map((s) => {
                  const pct = s.max ? Math.max(0, s.score) / s.max : 0;
                  const tone = toneFor(pct);
                  const Icon = ICON_BY_KEY[s.key] || Info;
                  return (
                    <div key={s.key} className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`win-explainer-signal-${s.key}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <div className={`w-6 h-6 rounded flex items-center justify-center border ${tone.tile}`}><Icon className="w-3 h-3" /></div>
                          <div className="font-display font-semibold text-sm">{s.label}</div>
                        </div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
                          <span className={tone.tile + " border rounded px-1.5 py-0.5"}>{tone.label}</span>
                          <span className="ml-2">{Math.max(0, s.score)}/{s.max}</span>
                        </div>
                      </div>
                      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden mb-1.5">
                        <div className={`h-full ${tone.bar}`} style={{ width: `${Math.round(pct * 100)}%` }} />
                      </div>
                      <div className="text-[12px] text-slate-600 leading-snug">{s.note}</div>
                    </div>
                  );
                })}
              </div>

              <details className="text-[12px] text-slate-500" data-testid="win-explainer-all">
                <summary className="cursor-pointer text-[11px] font-mono uppercase tracking-widest text-slate-500 hover:text-slate-800">Show all signals</summary>
                <div className="space-y-1 mt-2">
                  {(data.signals || []).map((s) => (
                    <div key={s.key} className="flex items-center justify-between border-t border-slate-100 py-1">
                      <span>{s.label}</span>
                      <span className="font-mono">{Math.max(0, s.score)}/{s.max}</span>
                    </div>
                  ))}
                </div>
              </details>
              <div className="text-[10px] text-slate-400 mt-3 italic">Deterministic breakdown — no credit spend, no LLM call.</div>
            </>
          )}
        </div>
      )}
    </span>
  );
}
